import {
  MAX_GAME_TURNS,
  MAX_QUIET_TURNS,
  holdsAllPowerPoints,
  type FactionId,
  type GameContext,
  type GameState,
} from '@archon3d/core';

/** How a game came to its end: won on the power points or by clearing the board, or drawn. */
export type MatchEnd =
  'power-points' | 'elimination' | 'agreed' | 'quiet' | 'turns' | 'stalemate' | 'mutual';

export interface SideSummary {
  id: FactionId;
  /** The board's name for the side, after the person's when a person played it. */
  name: string;
  /** Pieces on the board at the end, and pieces lost for good (one revived is not). */
  standing: number;
  lost: number;
  eliminated: boolean;
}

/** What a finished game comes to, for the screen after its last move. */
export interface MatchSummary {
  winner: FactionId | 'draw';
  end: MatchEnd;
  /** The turn and the round the game ended in, counted as the sign counts them, from 1. */
  turn: number;
  round: number;
  sides: SideSummary[];
}

/** The summary of a game that has ended; null while it runs. */
export function matchSummary(
  ctx: GameContext,
  state: GameState,
  people: Partial<Record<FactionId, string>> = {},
): MatchSummary | null {
  if (state.phase !== 'ended' || state.winner === null) return null;
  const { winner } = state;
  const standing = state.factions.filter((f) => !f.eliminated);
  // The core ends a game for one of these, tried in this order (`endTurn`); a win says which by
  // the board it leaves, and a draw by the counters that ran out.
  const end: MatchEnd =
    winner !== 'draw'
      ? holdsAllPowerPoints(ctx, state, winner)
        ? 'power-points'
        : 'elimination'
      : state.agreedDraw
        ? 'agreed'
        : standing.length === 0
          ? 'mutual'
          : state.turn - state.lastProgressTurn >= MAX_QUIET_TURNS
            ? 'quiet'
            : state.turn >= MAX_GAME_TURNS
              ? 'turns'
              : 'stalemate';
  const sides = ctx.board.def.factions.map((f): SideSummary => {
    const side = state.factions.find((s) => s.id === f.id);
    const person = people[f.id];
    return {
      id: f.id,
      name: person ? `${person} (${f.name})` : f.name,
      // A summoned elemental is a spell on the board, not one of the side's pieces.
      standing: state.pieces.filter(
        (p) => p.faction === f.id && ctx.roster[p.character]?.summoned !== true,
      ).length,
      lost: side?.graveyard.length ?? 0,
      eliminated: side?.eliminated ?? false,
    };
  });
  return { winner, end, turn: state.turn + 1, round: state.round + 1, sides };
}

/** The line under the result: how the game was won, or why nobody won it. */
export function endReason(summary: MatchSummary): string {
  switch (summary.end) {
    case 'power-points':
      return 'Every power point held at once.';
    case 'elimination':
      return 'Every other side taken off the board.';
    case 'agreed':
      return 'The sides agreed to a draw.';
    case 'quiet':
      return `${MAX_QUIET_TURNS} turns passed without a fight.`;
    case 'turns':
      return `The game reached turn ${MAX_GAME_TURNS}.`;
    case 'stalemate':
      return 'No side could move.';
    case 'mutual':
      return 'The last pieces fell together.';
  }
}

/** The result's heading: who won, or a draw. */
export function resultTitle(summary: MatchSummary): string {
  if (summary.winner === 'draw') return 'A draw';
  const side = summary.sides.find((s) => s.id === summary.winner);
  return `${side?.name ?? summary.winner} wins`;
}
